import '.a{display:flex;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.e{width:100vw;}.ssr-plugin'
import '.b{height:100vh;}.ssr-plugin'
import '.f{background:rgba(1,1,37,1);}.ssr-plugin'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { styled } from 'inlines'
import { useQuery } from '@based/sdk'
import { Language } from '@/apps/cms/components/SiteLanguages'
import useLocalStorage from '@based/use-local-storage'
import { useDependency } from '@based/ui'
import { Spinner } from '../components/Spinner'
import { getLanguageName } from '../utils/getLanguageName'
import { useTrack } from '../hooks/useTrack'

export type AppCtxProps = {
  content?: 'live-content' | 'preview-content'
  languages: Language[]
  activeLanguage: string
  votes: Record<number, number>
  votesCount: number
  setVotes: React.Dispatch<React.SetStateAction<Record<number, number>>>
  price: number
  currency: string
  maxVotes: number
  minVotes?: number
  onEvent: ReturnType<typeof useTrack>
}

const AppCtx = createContext<AppCtxProps>({
  content: 'live-content',
  languages: [],
  activeLanguage: 'en',
  votes: {},
  votesCount: 0,
  setVotes: () => {},
  price: 99,
  currency: 'eur',
  maxVotes: 20,
  minVotes: 0,
  onEvent: () => {},
})

export const useAppCtx = () => useContext(AppCtx)

export const AppCtxProvider: FC<
  { children: ReactNode } & Partial<Pick<AppCtxProps, 'content'>>
> = ({ children, content }) => {
  const detectedContent =
    content ||
    (typeof window !== 'undefined' &&
    window.location.pathname.includes('/preview')
      ? 'preview-content'
      : 'live-content')
  const [storageActiveLanguage, setStorageActiveLanguage] =
    useLocalStorage('language')
  // @ts-ignore
  const editionWindow = window?.DATA?.edition
  const {
    data: config = {},
    checksum: configChecksum,
    loading: configLoading,
    error: configError,
  } = useQuery(editionWindow !== undefined ? 'edition-config' : '', {
    editionId: editionWindow,
    $$lang: storageActiveLanguage ?? 'en',
  })

  let parsedLanguages: {
    id: number
    value: string
    label: string
    default?: boolean
  }[]
  // That issue with JSON returning as a string still exists
  if (typeof config?.languages === 'string') {
    try {
      parsedLanguages = JSON.parse(config.languages)
    } catch {
      parsedLanguages = []
    }
  } else {
    parsedLanguages = config?.languages || []
  }

  useEffect(() => {
    if (
      !configLoading &&
      !parsedLanguages.find(
        (language: { value: string }) =>
          language.value === storageActiveLanguage,
      )
    ) {
      const getDefaultLanguage = () => {
        const defaultLanguage = parsedLanguages.find(
          (language) => language.default,
        )
        if (defaultLanguage) {
          return defaultLanguage.value
        }
        if (typeof window !== 'undefined') {
          const browserLanguage = navigator.language?.split('-')[0]
          if (browserLanguage) {
            return browserLanguage
          }
        }
        return 'en'
      }

      setStorageActiveLanguage(getDefaultLanguage())
    }
  }, [configLoading, storageActiveLanguage, parsedLanguages])

  const {
    data: sequence = {},
    loading: sequenceLoading,
    error: sequenceError,
  } = useQuery(editionWindow !== undefined ? detectedContent : '', {
    id: editionWindow,
    content: 'sequence',
    lang: storageActiveLanguage ?? 'en',
  })

  const onEvent = useTrack({
    editionId: editionWindow,
    sequenceType: sequence?.sequenceType,
  })

  const hasFiredPageLoad = useRef(false)
  useEffect(() => {
    if (sequenceLoading || hasFiredPageLoad.current) return
    hasFiredPageLoad.current = true
    onEvent('page-load', {
      path: window.location.pathname,
      hostname: window.location.hostname,
    })
  }, [sequenceLoading])

  const [votes, setVotes] = useLocalStorage(
    editionWindow && sequence?.id
      ? `votes-${editionWindow}-${sequence?.id}-${detectedContent}`
      : undefined,
  )
  const votesCount = Object.values(
    (votes || {}) as AppCtxProps['votes'],
  ).reduce((a, b) => a + b, 0)

  const languages: any[] = []
  for (const lang of parsedLanguages) {
    if (lang.value) {
      // this makes storing the label in the DB kind of pointless but what can you do
      languages.push({ ...lang, label: getLanguageName(lang.value) })
    }
  }

  const ctx: AppCtxProps = useMemo(
    () => ({
      content: detectedContent,
      languages,
      activeLanguage: storageActiveLanguage,
      votes: votes ?? {},
      votesCount,
      setVotes,
      currency: config.currency,
      maxVotes: config.maxVotes,
      minVotes: config.minVotes,
      price: config.price,
      onEvent,
    }),
    [
      detectedContent,
      configChecksum,
      storageActiveLanguage,
      useDependency(votes || {}),
      votesCount,
      setVotes,
    ],
  )

  if (configLoading || configError || sequenceLoading || sequenceError) {
    return (
      <div className="a d c e b f"
      >
        <Spinner variant="white" />
      </div>
    )
  }

  return <AppCtx.Provider value={ctx}>{children}</AppCtx.Provider>
}
